import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { FC, useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useTranslation } from 'react-i18next';
import {
  normalizeHtmlQuill,
  normalizeQuillHtml,
} from 'views/email-templates/components/notificationSelected';
import { OrderNotesTypes } from '../types';

const useStyles = makeStyles((theme) => ({
  quillContainer: {
    display: 'flex',
    flexDirection: 'column',
    '& .ql-toolbar': {
      position: 'sticky',
      top: 0,
      zIndex: 10,
      flexShrink: 0,
    },
    '& .ql-container': {
      fontSize: '16px',
      flex: 1,
      overflow: 'auto',
      minHeight: '250px',
    },
    '& .ql-editor': {
      minHeight: '250px',
    },
  },
}));

interface AddNoteModalProps {
	open: boolean;
	handleClose: () => void;
	handleCreateNote: (content: string, critical: boolean) => void;
	note?: OrderNotesTypes | null;
	handleUpdateNote: (content: string, critical: boolean, noteId: string, resolved: boolean) => void;
	createNoteLoading?: boolean;
	updateNoteLoading?: boolean;
}

const AddNoteModal: FC<AddNoteModalProps> = ({
  open,
  handleClose,
  handleCreateNote,
  note,
  handleUpdateNote,
  createNoteLoading = false,
  updateNoteLoading = false,
}) => {
  const { t } = useTranslation('orders');
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [value, setValue] = useState(() => note ? normalizeHtmlQuill(note.content) : '');
  const [critical, setCritical] = useState(note?.critical ?? false);
  const [resolved, setResolved] = useState(note?.resolved ?? false);

  const quillRef = useRef<ReactQuill>(null);
  const isLoading = createNoteLoading || updateNoteLoading;

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      editor.setSelection(0, 0);
    }
  }, []);

  const handleChange = (event, kind) => {
    if (kind === 'critical') {
      setCritical(event.target.checked);
    } else {
      setResolved(event.target.checked);
    }
  };

  const handleSubmit = () => {
    const finalHtml = normalizeQuillHtml(value);
    if (note) {
      handleUpdateNote(finalHtml, critical, note._id, resolved);
    } else {
      handleCreateNote(finalHtml, critical);
    }
  };

  const handleDialogClose = (event: React.SyntheticEvent, reason?: string) => {
    if (isLoading || reason === 'backdropClick') {
      return;
    }
    handleClose();
  };

  return (
    <Dialog maxWidth={fullScreen ? undefined : 'lg'} fullWidth open={open} onClose={handleDialogClose} fullScreen={fullScreen} disableEscapeKeyDown={isLoading}>
      {note ? <DialogTitle>{t('newUpdateTitle')}</DialogTitle> : <DialogTitle>{t('newNoteTitle')}</DialogTitle>}

      <DialogContent>
        <Box className={classes.quillContainer}>
          <ReactQuill
            theme="snow"
            ref={quillRef}
            value={value}
            onChange={setValue}
            readOnly={isLoading}
            modules={{
						  toolbar: [
						    [{ size: [] }, { align: [] }],
						    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
						    [{ color: [] }, { background: [] }],
						    [{ list: 'ordered' }, { list: 'bullet' }],
						    ['link'],
						    ['clean'],
						  ],
            }}
            formats={[
						  'size',
						  'align',
						  'bold',
						  'italic',
						  'underline',
						  'strike',
						  'blockquote',
						  'color',
						  'background',
						  'list',
						  'bullet',
						  'link',
						  'clean',
            ]}
          />
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <FormControlLabel
            control={
              <Checkbox
                checked={critical}
                onChange={(e) => handleChange(e, 'critical')}
                name="critical"
                color="secondary"
                disabled={resolved || isLoading}
              />
						}
            label={t('criticalLabel')}
          />
          {critical && (
          <FormControlLabel
            control={
              <Checkbox
                checked={resolved}
                onChange={(e) => handleChange(e, 'resolved')}
                name="resolved"
                color="secondary"
                disabled={isLoading}
              />
							}
            label={t('resolvedLabel')}
          />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose()} color="primary" disabled={isLoading}>
          {t('closeButton')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="secondary" disabled={isLoading} startIcon={isLoading ? <FontAwesomeIcon icon="spinner" spin /> : undefined}>
          {t('acceptButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNoteModal;
